import { useEffect, useState } from 'react'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { apiFetch } from '../api/client'

interface BillingMe {
  org_id: string
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  stripe_subscription_status: string | null
}

const ALLOWED = new Set(['active', 'trialing'])
const POLL_MS = 1500
const MAX_ELAPSED_MS = 30_000

export function BillingReturnPage() {
  const [status, setStatus] = useState<string | null>(null)
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    let cancelled = false
    const start = Date.now()

    async function tick() {
      if (cancelled) return
      try {
        const me = await apiFetch<BillingMe>('/api/billing/me')
        if (cancelled) return
        setStatus(me.stripe_subscription_status)
        if (me.stripe_subscription_status && ALLOWED.has(me.stripe_subscription_status)) {
          window.location.assign('/')
          return
        }
      } catch {
        // keep polling — webhook may still be in flight
      }
      if (Date.now() - start > MAX_ELAPSED_MS) {
        setTimedOut(true)
        return
      }
      setTimeout(tick, POLL_MS)
    }
    tick()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="min-h-screen bg-background text-text-primary">
      <main className="mx-auto max-w-md px-6 py-16">
        <div className="space-y-4 rounded-xl border border-border bg-surface p-8 text-center shadow-lg">
          {timedOut ? (
            <>
              <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
              <h1 className="text-lg font-semibold">Subscription pending</h1>
              <p className="text-sm text-text-secondary">
                Stripe is still finalizing the subscription. You can return to the
                dashboard now — the gate will release automatically once the
                webhook lands.
              </p>
              <button
                onClick={() => window.location.assign('/')}
                className="mt-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-background"
              >
                Go to dashboard
              </button>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
              <h1 className="text-lg font-semibold">Activating your trial...</h1>
              <p className="text-sm text-text-secondary">
                Waiting for Stripe to confirm the subscription. This usually
                takes a few seconds.
              </p>
              <p className="text-xs text-text-tertiary">
                Current status: {status ?? 'pending'}
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
